import React, { useState } from 'react';
import { useCoffee } from '../context/CoffeeContext';
import { FlavorProfile } from './FlavorProfile';
import { ChevronDownIcon, ChevronUpIcon, TrashIcon, CheckSquareIcon, SquareIcon } from 'lucide-react';
import { ComparisonModal } from './ComparisonModal';
export const CoffeeList: React.FC = () => {
  const {
    coffeeEntries,
    coffeeBags,
    deleteCoffeeEntry
  } = useCoffee();
  const [expandedEntry, setExpandedEntry] = useState<string | null>(null);
  const [selectedEntries, setSelectedEntries] = useState<string[]>([]);
  const [showComparisonModal, setShowComparisonModal] = useState(false);
  const getBagName = (bagId: string) => {
    const bag = coffeeBags.find(b => b.id === bagId);
    return bag ? bag.name : 'Café Desconocido';
  };
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };
  const formatTime = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
  };
  const toggleExpand = (id: string) => {
    setExpandedEntry(expandedEntry === id ? null : id);
  };
  const toggleSelectEntry = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedEntries(prev => prev.includes(id) ? prev.filter(entryId => entryId !== id) : [...prev, id]);
  };
  const handleCompare = () => {
    if (selectedEntries.length >= 2) {
      setShowComparisonModal(true);
    }
  };
  if (coffeeEntries.length === 0) {
    return <div className="max-w-md mx-auto p-4 bg-white rounded-lg shadow text-center" data-id="element-299">
        <p className="text-amber-800" data-id="element-300">Aún no has registrado preparaciones.</p>
      </div>;
  }
  return <div className="max-w-md mx-auto space-y-4" data-id="element-301">
      <h2 className="text-xl font-bold mb-4 text-amber-900" data-id="element-302">
        Historial de Preparaciones
      </h2>
      {selectedEntries.length > 0 && <div className="sticky top-0 z-10 bg-amber-100 p-3 rounded-lg shadow-md flex justify-between items-center mb-4" data-id="element-303">
          <div className="flex items-center text-sm" data-id="element-304">
            <span className="text-amber-800 font-medium mr-2" data-id="element-305">
              {selectedEntries.length} preparación
              {selectedEntries.length > 1 ? 'es' : ''} seleccionada
              {selectedEntries.length > 1 ? 's' : ''}
            </span>
            {selectedEntries.length === 1 && <span className="text-amber-600 text-xs" data-id="element-306">
                Selecciona 1 café más
              </span>}
          </div>
          <button onClick={handleCompare} disabled={selectedEntries.length < 2} className={`px-4 py-2 rounded-md ${selectedEntries.length >= 2 ? 'bg-amber-600 text-white hover:bg-amber-700' : 'bg-amber-300 text-amber-700 cursor-not-allowed'}`} data-id="element-307">
            Comparar
          </button>
        </div>}
      {coffeeEntries.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()).map(entry => <div key={entry.id} className="bg-white rounded-lg shadow overflow-hidden" data-id="element-308">
            <div className="p-4 flex justify-between items-center cursor-pointer" onClick={() => toggleExpand(entry.id)} data-id="element-309">
              <div className="flex items-center" data-id="element-310">
                <div onClick={e => toggleSelectEntry(entry.id, e)} className="mr-3 text-amber-600" data-id="element-311">
                  {selectedEntries.includes(entry.id) ? <CheckSquareIcon size={20} data-id="element-312" /> : <SquareIcon size={20} data-id="element-313" />}
                </div>
                <div data-id="element-314">
                  <h3 className="font-medium text-amber-800" data-id="element-315">
                    {getBagName(entry.bagId)}
                  </h3>
                  <p className="text-sm text-gray-600" data-id="element-316">
                    {formatDate(entry.date)}
                  </p>
                  <p className="text-sm" data-id="element-317">
                    {entry.brewMethod} • {entry.coffeeWeight}g:
                    {entry.waterWeight}ml
                  </p>
                </div>
              </div>
              <div data-id="element-318">
                {expandedEntry === entry.id ? <ChevronUpIcon className="text-amber-600" size={20} data-id="element-319" /> : <ChevronDownIcon className="text-amber-600" size={20} data-id="element-320" />}
              </div>
            </div>
            {expandedEntry === entry.id && <div className="px-4 pb-4 border-t border-amber-100 pt-2" data-id="element-321">
                <div className="grid grid-cols-2 gap-2 mb-3" data-id="element-322">
                  <div data-id="element-323">
                    <span className="text-xs text-amber-700" data-id="element-324">Ratio</span>
                    <p className="font-medium" data-id="element-325">
                      1:{(entry.waterWeight / entry.coffeeWeight).toFixed(1)}
                    </p>
                  </div>
                  <div data-id="element-326">
                    <span className="text-xs text-amber-700" data-id="element-327">Molienda</span>
                    <p className="font-medium" data-id="element-328">{entry.grindSize}</p>
                  </div>
                  <div data-id="element-329">
                    <span className="text-xs text-amber-700" data-id="element-330">Temperatura</span>
                    <p className="font-medium" data-id="element-331">{entry.waterTemperature}°C</p>
                  </div>
                  <div data-id="element-332">
                    <span className="text-xs text-amber-700" data-id="element-333">Tiempo</span>
                    <p className="font-medium" data-id="element-334">
                      {formatTime(entry.totalBrewTime)}
                    </p>
                  </div>
                  <div data-id="element-335">
                    <span className="text-xs text-amber-700" data-id="element-336">TDS</span>
                    <p className="font-medium" data-id="element-337">{entry.tds}%</p>
                  </div>
                  <div data-id="element-338">
                    <span className="text-xs text-amber-700" data-id="element-339">Extracción</span>
                    <p className="font-medium" data-id="element-340">
                      {entry.extractionYield.toFixed(2)}%
                    </p>
                  </div>
                </div>
                {entry.pourDetails && <div className="mb-3" data-id="element-341">
                    <span className="text-xs text-amber-700" data-id="element-342">
                      Detalles de Vertidos
                    </span>
                    <p className="text-sm whitespace-pre-line" data-id="element-343">
                      {entry.pourDetails}
                    </p>
                  </div>}
                <div className="mb-3" data-id="element-344">
                  <span className="text-xs text-amber-700" data-id="element-345">
                    Perfil de Sabor
                  </span>
                  <FlavorProfile profile={entry.flavorNotes} setProfile={() => {}} readOnly={true} data-id="element-346" />
                </div>
                {entry.additionalNotes && <div className="mb-3" data-id="element-347">
                    <span className="text-xs text-amber-700" data-id="element-348">Notas</span>
                    <p className="text-sm whitespace-pre-line" data-id="element-349">
                      {entry.additionalNotes}
                    </p>
                  </div>}
                <div className="flex justify-end mt-2" data-id="element-350">
                  <button onClick={() => {
            if (confirm('¿Estás seguro de que quieres eliminar esta preparación?')) {
              deleteCoffeeEntry(entry.id);
            }
          }} className="flex items-center text-red-500 px-3 py-1 hover:bg-red-50 rounded-md" data-id="element-351">
                    <TrashIcon size={16} className="mr-1" data-id="element-352" />
                    <span data-id="element-353">Eliminar</span>
                  </button>
                </div>
              </div>}
          </div>)}
      {showComparisonModal && <ComparisonModal selectedEntries={selectedEntries} onClose={() => {
      setShowComparisonModal(false);
      setSelectedEntries([]);
    }} data-id="element-354" />}
    </div>;
};